import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { bulkUploadAudio } from "./api";

// Audio bulk upload, used by Podcast and News: each selected audio file becomes one
// entry, titled with the file name. Everything else is filled in later from the edit form.

const ALLOWED_EXTENSIONS = ["mp3", "wav"]; // same as the Create Podcast form
const MAX_FILES = 20; // matches PodcastAudioBulkUploadRequest::MAX_FILES

const getExtension = (name) => name.slice(name.lastIndexOf(".") + 1).toLowerCase();

const getTitle = (name) => {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name).trim();
};

const useAudioBulkUpload = ({ endpoint, entityLabel = "podcast", refetch, handleClose }) => {
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const resetForm = () => {
    setItems([]);
    setProgress(0);
    setIsUploading(false);
    setErrorMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = "";
    setErrorMessage("");
    const rejected = [];
    const accepted = [];
    selected.forEach((file, index) => {
      if (!ALLOWED_EXTENSIONS.includes(getExtension(file.name))) {
        rejected.push(file.name);
        return;
      }
      // ignore files already in the list
      if (items.some((item) => item.name === file.name && item.size === file.size)) return;
      accepted.push({
        id: `${Date.now()}-${index}-${file.name}`,
        file,
        name: file.name,
        size: file.size,
        title: getTitle(file.name),
      });
    });
    if (rejected.length > 0) {
      toast.error(`Skipped ${rejected.length} file(s). Only MP3 and WAV files are allowed.`);
    }
    setItems((prev) => {
      const next = [...prev, ...accepted];
      if (next.length > MAX_FILES) {
        toast.error(`A maximum of ${MAX_FILES} files can be uploaded at a time.`);
        return next.slice(0, MAX_FILES);
      }
      return next;
    });
  };

  const handleRemove = (id) => setItems((prev) => prev.filter((item) => item.id !== id));

  const handleUpload = async () => {
    if (items.length === 0) {
      toast.error("Select at least one audio file");
      return;
    }
    setIsUploading(true);
    setProgress(0);
    setErrorMessage("");

    const formData = new FormData();
    items.forEach((item) => formData.append("files[]", item.file));

    const response = await bulkUploadAudio(endpoint, formData, (event) => {
      if (event.total) setProgress(Math.round((event.loaded * 100) / event.total));
    });
    setIsUploading(false);

    if (response?.status === 200 && response?.data?.success) {
      const queued = response?.data?.data?.queued_files ?? items.length;
      const failed = response?.data?.data?.failed_files ?? [];
      toast.success(
        `${queued} ${entityLabel}(s) uploaded. They are added as inactive; add the remaining details from the list once the audio has been processed.`
      );
      if (failed.length > 0) {
        toast.error(`${failed.length} file(s) could not be uploaded: ${failed.join(", ")}`);
      }
      resetForm();
      handleClose();
      refetch?.();
    } else {
      const errors = response?.errors ?? response?.data?.errors;
      const message =
        (errors && Object.values(errors).flat()[0]) ??
        response?.message ??
        response?.data?.message ??
        "Something went wrong";
      setErrorMessage(message);
      toast.error(message);
    }
  };

  const handleCancel = () => {
    resetForm();
    handleClose();
  };

  return {
    fileInputRef,
    items,
    progress,
    isUploading,
    errorMessage,
    acceptTypes: ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(","),
    handleFileChange,
    handleRemove,
    handleUpload,
    handleCancel,
  };
};

export default useAudioBulkUpload;
